import type { ChapterData } from "./content-types";

/**
 * CASO EXTRA — "Peso muerto" (Polígono Logístico · Almacén).
 * Sector independiente (post-misterio): entrenamiento de manipulación manual
 * de cargas, orden y limpieza, estanterías y carretillas.
 * Mecánica: LEVANTAR LA CARGA EN ORDEN (reutiliza el tipo "lockout").
 */
export const POLIGONO: ChapterData = {
  id: "poligono",
  title: "Peso muerto",
  location: "poligono",
  secStart: 45,
  secGreat: 84,
  secOk: 68,
  secBad: 56,
  needTalk: 2,
  needHazards: 3,

  interactables: [
    { id: "carga", type: "machine", pos: { x: 6, y: 5 }, label: "Caja pesada", verb: "use", ref: "carga", radius: 1.6, color: 0xb07a3a },
    { id: "estanteria", type: "object", pos: { x: 3, y: 3 }, label: "Estantería sobrecargada", verb: "inspect", ref: "estanteria", radius: 1.6, color: 0x8a6a3a },
    { id: "carretilla", type: "vehicle", pos: { x: 11, y: 6 }, label: "Carretilla elevadora", verb: "inspect", ref: "carretilla", radius: 1.8, color: 0xe0a01a },
    { id: "derrame", type: "object", pos: { x: 8, y: 8 }, label: "Mancha de aceite", verb: "inspect", ref: "derrame", radius: 1.4, color: 0x3a3630 },
    { id: "pale", type: "object", pos: { x: 5, y: 7 }, label: "Palé en el paso", verb: "inspect", ref: "pale", radius: 1.4, color: 0xa9843f },
    { id: "albaran", type: "document", pos: { x: 12, y: 3 }, label: "Albarán de carga", verb: "read", ref: "albaran", radius: 1.4 },
    { id: "rocio", type: "npc", pos: { x: 4, y: 6 }, label: "Rocío", verb: "talk", ref: "rocio", radius: 1.5, color: 0xb23a6a },
    { id: "damian", type: "npc", pos: { x: 10, y: 8 }, label: "Youssef", verb: "talk", ref: "damian", radius: 1.5, color: 0x2b5f3f },
    { id: "taquilla", type: "locker", pos: { x: 2, y: 9 }, label: "Taquilla de EPIs", verb: "open", radius: 1.6 },
    { id: "salida", type: "door", pos: { x: 13, y: 10 }, label: "Salir a la ciudad", verb: "open", radius: 1.7 },
  ],

  hotspots: {
    carga: {
      label: "Caja pesada",
      text: "Una <b>caja bien cargada</b> que hay que subir a la estantería. Pesa. En el almacén nadie se rompe la espalda de golpe: se rompe a base de <b>levantar mal</b>, un día tras otro. Hay una forma correcta de hacerlo.",
      note: { section: "pistas", heading: "Caja pesada para elevar", body: "Carga que hay que manipular manualmente. La técnica correcta de levantamiento evita el daño lumbar por sobreesfuerzo." },
      hazardId: "h_carga",
      comment: "Levantar con la espalda es fácil... hasta que un día te quedas doblado y ya no te enderezas. Con las piernas, siempre.",
    },
    estanteria: {
      label: "Estantería sobrecargada",
      text: "La <b>estantería</b> va cargada muy por encima de su límite, con las cajas pesadas <b>arriba</b> y sin <b>anclar a la pared</b>. Un empujón y se viene abajo entera, con todo lo que lleva encima.",
      note: { section: "pistas", heading: "Estantería inestable y sobrecargada", body: "Estantería por encima de su carga máxima, peso mal repartido (lo pesado arriba) y sin anclar. Riesgo de vuelco y caída de objetos." },
      hazardId: "h_estanteria",
      comment: "Lo pesado arriba, sin anclar, y a reventar. Eso no es una estantería, es una trampa esperando el momento.",
      signs: ["w_objetos", "m_casco"],
      assess: {
        identify: {
          q: "¿Cuál es el riesgo principal de esta estantería?",
          options: [
            { text: "Vuelco y caída de objetos: sobrecargada, mal repartida y sin anclar", ok: true },
            { text: "Riesgo eléctrico por los focos de arriba", ok: false },
            { text: "Riesgo de incendio de las cajas", ok: false },
          ],
          hint: "Fíjate en tres cosas: cuánto lleva, dónde está lo pesado y si está sujeta a la pared.",
        },
        control: {
          q: "¿Cuál es la mejor medida, según la jerarquía preventiva?",
          options: [
            { text: "Respetar la carga máxima, poner lo pesado abajo y anclar la estantería a la pared", tier: "eliminar", grade: "best", why: "Repartir bien el peso (lo pesado abajo), no pasar del límite y anclar elimina el riesgo de vuelco de raíz. Es protección estructural, no un parche." },
            { text: "Dejarla así pero acordonar la zona con cinta", tier: "organizativa", grade: "ok", why: "Delimitar reduce la exposición, pero la estantería sigue inestable: es un parche, no la solución. Hay que descargarla y anclarla." },
            { text: "Seguir apilando arriba, que aún cabe", tier: "ninguna", grade: "bad", why: "Añadir peso arriba sube el centro de gravedad y acerca el vuelco.", danger: "Al coger una caja, la estantería bascula y te sepulta bajo el material." },
          ],
        },
        apply: {
          intro: "Estabiliza la estantería como es debido. Haz los pasos en orden.",
          steps: [
            "Descargar hasta no pasar de la carga máxima indicada",
            "Colocar lo más pesado en los estantes de abajo",
            "Anclar la estantería a la pared con sus fijaciones",
            "Señalizar la carga máxima admisible por balda",
          ],
          wrong: "Ese paso todavía no. Primero se quita el exceso de peso y se reparte; anclar una estantería reventada solo fija el problema en su sitio.",
          success: "Carga dentro del límite, peso abajo y estantería anclada: ya no hay vuelco que temer.",
        },
        note: { section: "expedientes", heading: "Estanterías: carga, reparto y anclaje", body: "No superar la carga máxima, colocar lo pesado en las baldas bajas y anclar a la pared o entre módulos. Señalizar la carga admisible." },
      },
    },
    carretilla: {
      label: "Carretilla elevadora",
      text: "La <b>carretilla</b> circula por el mismo pasillo que la gente, <b>sin separación</b> ni señalización de paso peatonal. Va deprisa, con la <b>carga alta</b> que tapa la visión. Aquí un atropello es cuestión de tiempo.",
      note: { section: "pistas", heading: "Carretilla y peatones sin separar", body: "Circulación de carretilla mezclada con peatones, sin pasos señalizados, a velocidad y con la carga elevada que resta visibilidad. Riesgo de atropello y vuelco." },
      hazardId: "h_carretilla",
      comment: "Una carretilla y peatones en el mismo pasillo, sin líneas, sin avisos. Tarde o temprano se cruzan en el peor momento.",
      signs: ["w_atrapamiento", "m_chaleco", "p_paso"],
      assess: {
        identify: {
          q: "¿Qué hace peligrosa la circulación de la carretilla aquí?",
          options: [
            { text: "Comparte pasillo con los peatones sin separación ni señalización", ok: true },
            { text: "Vuelco de la carretilla al girar con la carga alta", ok: false },
            { text: "Caída de la carga que circula elevada", ok: false },
          ],
          hint: "Piensa en quién más pasa por ese pasillo y qué separa a las personas de la máquina. ¿Hay algo?",
        },
        control: {
          q: "¿Cuál es la mejor medida, según la jerarquía preventiva?",
          options: [
            { text: "Separar los recorridos: pasillos peatonales delimitados y aparte de los de carretilla", tier: "colectiva", grade: "best", why: "Segregar personas y máquinas con vías propias protege a todos a la vez, sin depender de que cada uno vaya con cuidado. Es protección colectiva." },
            { text: "Que el conductor toque el claxon en cada cruce y baje la carga", tier: "organizativa", grade: "ok", why: "Avisar y bajar la carga ayuda, pero sigue dependiendo de la atención de cada uno: si falla, hay atropello. Mejor separar los recorridos." },
            { text: "Que los peatones vayan con chaleco y ya se apañan", tier: "epi", grade: "bad", why: "El chaleco te hace más visible, pero no evita el atropello si compartís el mismo carril.", danger: "La carga elevada tapa la visión del conductor; no te ve a tiempo y la horquilla te alcanza." },
          ],
        },
        apply: {
          intro: "Separa personas y máquinas en el almacén. Haz los pasos en orden.",
          steps: [
            "Delimitar en el suelo un pasillo peatonal propio",
            "Señalizar los cruces y los pasos de peatones",
            "Limitar la velocidad de la carretilla en la zona",
            "Circular con la carga baja para no perder visión",
          ],
          wrong: "Ese paso todavía no. Primero se separan los recorridos y se señalizan; limitar la velocidad sin carriles definidos deja el atropello a medias.",
          success: "Peatones y carretillas por vías separadas, cruces señalizados y carga baja: el atropello deja de estar servido.",
        },
        note: { section: "expedientes", heading: "Carretillas: segregar personas y máquinas", body: "Separar físicamente los recorridos de peatones y de carretillas, señalizar cruces, limitar la velocidad y circular con la carga baja y buena visibilidad." },
      },
    },
    derrame: {
      label: "Mancha de aceite",
      text: "Una <b>mancha de aceite</b> en pleno paso, brillante y traicionera. Lleva ahí un rato: alguien ha puesto un cartón encima 'para no verla'. Un resbalón en un almacén con carga en brazos no acaba en un simple susto.",
      note: { section: "pistas", heading: "Derrame en el paso", body: "Charco de aceite en zona de tránsito, tapado con un cartón en vez de limpiarlo. Riesgo de resbalón, agravado si se transporta carga." },
      hazardId: "h_orden",
      clearOnResolve: true,
      comment: "Un cartón sobre el aceite. Clásico. No lo limpia, lo esconde; y el resbalón sigue ahí debajo esperando.",
      signs: ["w_suelo", "m_botas"],
      assess: {
        identify: {
          q: "¿Cuál es el riesgo de la mancha tapada con un cartón?",
          options: [
            { text: "Resbalón: el aceite sigue ahí y el cartón lo oculta sin quitarlo", ok: true },
            { text: "Intoxicación por respirar el aceite", ok: false },
            { text: "Contaminación del producto almacenado por el aceite", ok: false },
          ],
          hint: "Tapar no es limpiar. ¿Qué hay debajo del cartón cuando lo pisas con una caja en brazos?",
        },
        control: {
          q: "¿Cuál es la mejor medida, según la jerarquía preventiva?",
          options: [
            { text: "Limpiar el derrame al momento y corregir la fuga que lo causa", tier: "eliminar", grade: "best", why: "Quitar el aceite y atajar la fuga elimina el resbalón de raíz. Orden y limpieza no es cosmética: es prevención." },
            { text: "Señalizar 'suelo mojado' y dejar la limpieza para el final del turno", tier: "organizativa", grade: "ok", why: "La señal avisa, pero el charco sigue ahí durante horas. Mejor limpiarlo ya; señalizar es solo mientras tanto." },
            { text: "Poner otro cartón encima, que absorbe", tier: "ninguna", grade: "bad", why: "El cartón se empapa y resbala igual o más; el aceite sigue bajo los pies.", danger: "Pisas el cartón con una caja en brazos, patinas y caes de espaldas contra el palé." },
          ],
        },
        apply: {
          intro: "Deja el paso seguro. Haz los pasos en orden.",
          steps: [
            "Señalizar y acordonar la zona del derrame",
            "Absorber el aceite con material adecuado (no cartón)",
            "Limpiar y desengrasar el suelo hasta dejarlo seco",
            "Localizar y corregir la fuga que lo provoca",
          ],
          wrong: "Ese paso todavía no. Primero se avisa y se acordona para que nadie pise mientras limpias; y no se da por hecho hasta corregir la fuga.",
          success: "Zona señalizada, aceite retirado, suelo seco y fuga corregida: el resbalón se acabó, no se ha escondido.",
        },
        note: { section: "expedientes", heading: "Orden y limpieza: limpiar, no tapar", body: "Los derrames se limpian de inmediato con absorbente adecuado y se corrige la causa. Tapar o aplazar mantiene el riesgo de resbalón, la primera causa de accidente en almacén." },
      },
    },
    pale: {
      label: "Palé en el paso",
      text: "Un <b>palé vacío</b> atravesado en mitad del pasillo, con un fleje suelto enganchando los pies. El paso de emergencia, medio bloqueado. En un almacén, el <b>orden</b> no es estética: es por dónde sales corriendo si algo pasa.",
      note: { section: "pistas", heading: "Paso obstruido por un palé", body: "Palé y flejes abandonados en zona de tránsito y cerca de una salida. Riesgo de tropiezo y de bloqueo de la vía de evacuación." },
      hazardId: "h_orden",
      comment: "Un palé en medio del paso y un fleje suelto. Aquí se tropieza de día... y de noche, con prisa, es peor.",
      signs: ["w_suelo", "e_salida"],
    },
    albaran: {
      label: "Albarán de carga",
      text: "El <b>albarán</b> del turno: 'entrega urgente, priorizar tiempos'. Al margen, a boli: <b>«mozo de menos por baja — no se cubre»</b>. Menos manos y más prisa: la mezcla que llena de atajos el almacén.",
      note: { section: "expedientes", heading: "Prisa y falta de personal", body: "Entrega urgente con un mozo de baja sin cubrir. La presión de tiempo y la falta de manos empujan a manipular mal las cargas y a saltarse el orden." },
      comment: "Menos gente, más prisa, 'ya lo hacemos como sea'. Los atajos en un almacén se pagan con la espalda o con un pie.",
    },
  },

  npcs: {
    rocio: {
      name: "Rocío",
      role: "Mozo de almacén",
      portrait: "rocio",
      note: { section: "personas", heading: "Rocío", body: "Mozo de almacén. Arrastra dolores de espalda de levantar cajas a solas y con prisa. Pide medios, no milagros." },
      dialogue: [
        { speaker: "npc", text: "Ay, mi espalda. Llevo desde las seis subiendo cajas yo sola. Como falta gente, hay que ir 'a saco'." },
        { speaker: "epi", text: "Nadie debería cargar así. ¿Cómo lleváis el tema de los pesos?" },
        { choices: [ { text: "¿Os han enseñado a levantar con las piernas?", go: 3 }, { text: "¿Y si el peso es demasiado para una persona?", go: 4 } ] },
        { speaker: "npc", text: "En teoría sí: doblar rodillas, espalda recta, la carga pegada al cuerpo, subir con las piernas y girar con los pies, no con la cintura. En la práctica, con prisa, todos levantamos de cualquier manera. Y así estamos.", flag: "rocio_tecnica", note: { section: "pistas", heading: "La técnica correcta, ignorada por la prisa", body: "Rocío conoce la técnica (rodillas, espalda recta, carga pegada, subir con piernas, girar con los pies), pero la prisa lleva a levantar mal." }, end: true },
        { speaker: "npc", text: "Pues se pide ayuda o una ayuda mecánica: un transpalé, una carretilla, entre dos. Lo que no puede ser es que 'no llego a los tiempos' acabe siempre en 'me lo echo a la espalda yo'. Un día la espalda dice basta.", flag: "rocio_medios", note: { section: "pistas", heading: "Cargas pesadas: ayuda o medios mecánicos", body: "Ante cargas excesivas hay que usar ayudas mecánicas (transpalé, carretilla) o levantar entre varias personas, no forzar en solitario por cumplir tiempos." }, end: true },
      ],
    },
    damian: {
      name: "Youssef",
      role: "Carretillero",
      portrait: "damian",
      note: { section: "personas", heading: "Youssef", body: "Carretillero. Casi atropella a un compañero por circular entre peatones con la carga alta. Sabe que faltan pasillos separados." },
      dialogue: [
        { speaker: "npc", text: "¿Has visto lo del pasillo? Hoy casi me llevo por delante a un compañero. Iba con la carga alta y no lo vi hasta el último segundo." },
        { speaker: "epi", text: "Un susto de esos avisa. ¿Aquí van mezclados carretillas y gente?" },
        { choices: [ { text: "¿No hay pasillos separados para peatones?", go: 3 }, { text: "¿Por qué circulas con la carga tan alta?", go: 4 } ] },
        { speaker: "npc", text: "No hay líneas ni pasos peatonales, todo es el mismo carril. Yo pido a gritos que pinten los pasillos y separen la gente de las máquinas. Con un chaleco no basta si vamos por el mismo sitio.", flag: "damian_pasillos", note: { section: "pistas", heading: "Falta separación de recorridos", body: "Youssef reclama pasillos peatonales delimitados y separados de la circulación de carretillas. El chaleco no sustituye a la segregación de recorridos." }, end: true },
        { speaker: "npc", text: "Porque me meten prisa y no bajo a maniobrar en cada tramo. Está mal, lo sé: con la carga alta no veo lo que tengo delante. Debería circular con la carga baja y a poca velocidad, pero 'hay que sacar los pedidos'.", flag: "damian_carga", note: { section: "pistas", heading: "Circular con la carga alta resta visión", body: "Youssef reconoce que llevar la carga elevada le tapa la visibilidad. Debe circular con la carga baja y a velocidad reducida." }, end: true },
      ],
    },
  },

  hazards: [
    { id: "h_carga", category: "Sobreesfuerzo: manipulación manual de cargas", severity: 2, discoverable: true, requiredAction: "Levantar con la técnica correcta o usar ayuda mecánica" },
    { id: "h_estanteria", category: "Caída de objetos / vuelco de estantería", severity: 3, discoverable: true, requiredAction: "Repartir el peso, no exceder la carga y anclar" },
    { id: "h_carretilla", category: "Atropello por carretilla", severity: 3, discoverable: true, requiredAction: "Separar recorridos de peatones y máquinas" },
    { id: "h_orden", category: "Caídas al mismo nivel (orden y limpieza)", severity: 2, discoverable: true, requiredAction: "Limpiar derrames y despejar los pasos" },
  ],

  mission: {
    id: "extra_poligono",
    title: "Peso muerto",
    location: "poligono",
    objectives: [
      { id: "talk", type: "talk_workers", label: "Habla con la gente del almacén", target: "2" },
      { id: "hazards", type: "discover_hazards", label: "Detecta los riesgos del almacén", target: "3" },
      { id: "extra", type: "extra", label: "Levanta la carga con la técnica correcta", hidden: true },
      { id: "equip", type: "equip", label: "Ponte el equipo del almacén", hidden: true },
      { id: "report", type: "report", label: "Deja la carga colocada con seguridad", hidden: true },
    ],
  },

  epi: {
    needed: ["botas", "guantes", "chaleco"],
    optional: ["casco"],
    overkill: ["arnes", "mascarilla", "auditiva", "gafas"],
  },

  extra: {
    triggerId: "carga",
    type: "lockout",
    signs: ["m_botas", "m_guantes", "w_objetos"],
    label: "Levantar la carga en orden",
    steps: [
      { id: "planifica", text: "Planificar: valorar el peso y despejar la ruta hasta la estantería." },
      { id: "acerca", text: "Acercarse a la carga y separar los pies para una base estable." },
      { id: "flexiona", text: "Doblar las rodillas con la espalda recta (no doblar la cintura)." },
      { id: "agarra", text: "Agarrar firme y pegar la carga al cuerpo." },
      { id: "levanta", text: "Levantar con la fuerza de las piernas, subiendo despacio." },
      { id: "gira", text: "Girar moviendo los pies, nunca torciendo la cintura." },
      { id: "deposita", text: "Depositar la carga doblando de nuevo las rodillas." },
    ],
    wrong: "Ese paso todavía no. La clave: <b>nunca se levanta doblando la cintura</b> ni se gira con la espalda cargada. Empieza por planificar y colocar bien los pies.",
    success: "Carga levantada con las piernas, pegada al cuerpo y girando con los pies: así se hace cien veces al día sin dejarse la espalda.",
    note: { section: "expedientes", heading: "Manipulación manual: levantar con las piernas", body: "Planificar la ruta, pies separados, rodillas dobladas y espalda recta, carga pegada al cuerpo, subir con las piernas y girar con los pies. Ante cargas excesivas, ayuda mecánica o entre varios." },
  },

  consequence: {
    title: "Vuelves a por la carga",
    lines: {
      botas: "Sin botas con puntera, la caja al bajar te habría machacado el empeine.",
      guantes: "Sin guantes, el fleje y los cantos de la caja te habrían cortado las manos.",
      chaleco: "Sin alta visibilidad, la carretilla que cruza el pasillo no te habría visto a tiempo.",
    },
    great: "Con botas, guantes y chaleco, y levantando con las piernas, dejas la carga colocada sin un tirón ni un susto.",
    greatOver: "Arnés y mascarilla para subir una caja a una balda... 'ni que fueras a bajar a una mina, chiquillo'.",
    ok: "Sale bien, aunque con algún gesto forzado que, con lo que sabes, no tocaba.",
    bad: "Cargar así, sin equipo y a lo bruto, es justo como acaban las espaldas y los pies en el almacén.",
  },

  workId: "carga",
  resolveNotes: [
    { section: "expedientes", heading: "Caso: Peso muerto", body: "Almacén con manipulación manual incorrecta, estantería sobrecargada sin anclar, carretillas mezcladas con peatones y derrame sin limpiar. Raíz común: prisa y falta de medios. Resuelto con técnica correcta, orden y separación de recorridos." },
  ],
  resolveText:
    `<p>Con la técnica correcta —<b>piernas, espalda recta y carga pegada</b>— dejas la caja en su balda sin un tirón. La estantería, anclada; el paso, despejado; los pasillos, separados.</p>
     <div class="epi-callout">En el almacén casi nadie se hace daño de golpe. Se hace daño <b>despacio</b>: una espalda que se dobla mal mil veces, un paso que nadie limpia. La prevención aquí es <b>constancia</b>, no heroicidad.</div>`,
};
